/**
 * useTransactionAssist (M30.5.3) — calls the HR-initiate /assist
 * endpoint with a debounced trigger so the 5 AI panels populate
 * as HR fills the form.
 *
 * Generic over the body type so each form (Leave / Loan / Excuse /
 * T&A / Overtime / Contract Amendment / EOS) reuses the same
 * cadence + state-management:
 *
 *   - 500ms debounce so we don't fire on every keystroke
 *   - request-id guard so a stale response doesn't overwrite a
 *     newer one
 *   - exposes { result, isLoading, error, acknowledge }
 *
 * The form passes `enabled` to gate when the assist call should
 * fire (e.g. only after employee + reason_code are set).
 */

import { useEffect, useRef, useState } from "react";

import type { TransactionAssistOut } from "./api";

const DEBOUNCE_MS = 500;

export interface UseTransactionAssistOptions<B> {
  /** The body to send to /assist. Hook fires when this changes
   *  (after the debounce window). */
  body: B;
  /** When false, no call fires — used to gate the assist on
   *  prerequisites (employee picked + reason code chosen). */
  enabled: boolean;
  /** The actual endpoint call — different per transaction type. */
  callAssist: (body: B) => Promise<TransactionAssistOut>;
}

export interface UseTransactionAssistReturn {
  result: TransactionAssistOut | null;
  isLoading: boolean;
  error: string | null;
  /** Codes HR has explicitly acknowledged. The form passes this
   *  set into ai_findings_acknowledged at commit time. */
  acknowledgedCodes: Set<string>;
  acknowledge: (code: string) => void;
  unacknowledge: (code: string) => void;
  /** Bypass the debounce + fire immediately. */
  refresh: () => void;
}

export function useTransactionAssist<B>(
  options: UseTransactionAssistOptions<B>,
): UseTransactionAssistReturn {
  const { body, enabled, callAssist } = options;
  const [result, setResult] = useState<TransactionAssistOut | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acknowledgedCodes, setAcknowledgedCodes] = useState<
    Set<string>
  >(new Set());
  const requestId = useRef(0);
  const timer = useRef<number | null>(null);
  const bodyJson = JSON.stringify(body);

  useEffect(() => {
    if (!enabled) {
      setResult(null);
      return;
    }
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
    }
    timer.current = window.setTimeout(() => {
      const myId = ++requestId.current;
      setIsLoading(true);
      setError(null);
      callAssist(body)
        .then((r) => {
          if (myId !== requestId.current) return; // stale
          setResult(r);
          setIsLoading(false);
        })
        .catch((e) => {
          if (myId !== requestId.current) return;
          setError(String(e));
          setIsLoading(false);
        });
    }, DEBOUNCE_MS) as unknown as number;
    return () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
      }
    };
    // bodyJson is the stable key — re-fires only when the body
    // actually changes. callAssist + enabled are passed through
    // for completeness.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyJson, enabled]);

  return {
    result,
    isLoading,
    error,
    acknowledgedCodes,
    acknowledge: (code) =>
      setAcknowledgedCodes((s) => {
        const n = new Set(s);
        n.add(code);
        return n;
      }),
    unacknowledge: (code) =>
      setAcknowledgedCodes((s) => {
        const n = new Set(s);
        n.delete(code);
        return n;
      }),
    refresh: () => {
      if (!enabled) return;
      const myId = ++requestId.current;
      setIsLoading(true);
      setError(null);
      callAssist(body)
        .then((r) => {
          if (myId !== requestId.current) return;
          setResult(r);
          setIsLoading(false);
        })
        .catch((e) => {
          if (myId !== requestId.current) return;
          setError(String(e));
          setIsLoading(false);
        });
    },
  };
}
